import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, AlertTriangle, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ENTITY_LABEL = { comms: "Communications", curriculum: "Training curricula", impact: "Impact register starter rows", metric: "Adoption measures", condition: "Scorecard conditions", gate: "Readiness gates" };
const ORDER = ["comms", "curriculum", "impact", "metric", "condition", "gate"];

// Apply a System Rollout variant: preview everything, opt out per item, set milestone anchors, then merge into a new or existing change.
export default function RolloutApplyDialog({ variant, changeId, open, onClose, onApplied }) {
  const [pv, setPv] = useState(null);
  const [excl, setExcl] = useState(new Set());
  const [ms, setMs] = useState({});
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (!open || !variant) return;
    setPv(null); setExcl(new Set()); setTitle(variant.name);
    api.post(`/rollout/${variant.key}/preview`, { change_id: changeId || null, milestones: {} })
      .then((r) => { setPv(r.data); setMs(r.data.milestones || {}); setExcl(new Set(r.data.items.filter((i) => i.duplicate).map((i) => i.key))); })
      .catch((e) => toast.error(errMsg(e)));
  }, [open, variant, changeId]);

  const toggle = (k) => setExcl((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const refreshDates = (next) => {
    setMs(next);
    api.post(`/rollout/${variant.key}/preview`, { change_id: changeId || null, milestones: next }).then((r) => setPv((p) => ({ ...p, items: r.data.items.map((it) => ({ ...it })), has_go_live: r.data.has_go_live }))).catch(() => {});
  };

  const apply = async (confirm = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rollout/${variant.key}/apply`, { change_id: changeId || null, title, exclude: [...excl], milestones: ms, confirm });
      const added = Object.values(data.created).reduce((a, b) => a + b, 0);
      toast.success(`Applied ${variant.name} — ${added} item${added === 1 ? "" : "s"} added${data.skipped?.length ? `, ${data.skipped.length} already present` : ""}`, { description: "Edit anything, then use “Save as playbook” to make it your own." });
      onApplied?.(data.change_id);
      onClose();
      if (!changeId) nav(`/app/changes/${data.change_id}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(e.response.data.detail + "\n\nProceed and merge the new items? Your existing edits are kept.")) return apply(true);
      } else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const grouped = (pv?.items || []).reduce((acc, it) => { (acc[it.entity] = acc[it.entity] || []).push(it); return acc; }, {});
  const chosen = (pv?.items || []).filter((i) => !excl.has(i.key)).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rollout-dialog">
        <DialogHeader><DialogTitle className="font-head text-2xl font-extrabold flex items-center gap-2"><Rocket size={18} /> {variant?.name}</DialogTitle></DialogHeader>
        {(!pv || !variant) ? <p className="text-sm text-muted-foreground">Preparing preview…</p> : (
          <div className="space-y-4 text-sm">
            <div className="rounded-xl border border-warning-200 bg-warning-50 p-3 text-xs text-warning-800" data-testid="rollout-starting-point">
              <span className="font-bold">Starting point.</span> {variant.label}
            </div>
            {variant.warn && <div className="rounded-xl border border-danger-200 bg-danger-50 p-3 text-xs text-danger-800 flex gap-2" data-testid="rollout-warn"><AlertTriangle size={14} className="shrink-0 mt-0.5" /> {variant.warn}</div>}
            {pv.conflict && <div className="rounded-xl border border-danger-200 bg-danger-50 p-3 text-xs text-danger-800" data-testid="rollout-conflict">{pv.conflict}</div>}

            {!changeId && (
              <label className="block"><span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Change title</span>
                <input data-testid="rollout-title" className="pp-input mt-1" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
            )}

            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2"><CalendarClock size={13} /> Milestone anchors (dates this plan hangs off)</div>
              {!pv.has_go_live && <p className="text-xs text-warning-800 mb-2" data-testid="rollout-nogolive">No go-live date yet — items will be created unscheduled. Set go-live and the plan schedules itself.</p>}
              <div className="grid sm:grid-cols-2 gap-2">
                {pv.milestones && Object.keys(pv.milestones).map((a) => (
                  <label key={a} className="flex items-center gap-2 text-xs"><span className="w-32 text-muted-foreground capitalize">{a.replace(/_/g, " ")}</span>
                    <input type="date" data-testid={`rollout-anchor-${a}`} className="pp-input py-1 text-xs" value={ms[a] || ""} onChange={(e) => refreshDates({ ...ms, [a]: e.target.value })} /></label>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {ORDER.filter((e) => grouped[e]).map((e) => (
                <div key={e}>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">{ENTITY_LABEL[e]} ({grouped[e].length})</div>
                  <div className="space-y-1">
                    {grouped[e].map((it) => (
                      <label key={it.key} className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 ${excl.has(it.key) ? "border-border opacity-55" : "border-ink"}`}>
                        <input type="checkbox" data-testid={`rollout-item-${it.key}`} checked={!excl.has(it.key)} onChange={() => toggle(it.key)} className="h-4 w-4 mt-0.5" />
                        <span className="flex-1"><span className="font-semibold text-foreground">{it.label}</span><span className="block text-[11px] text-muted-foreground">{it.detail}{it.date ? ` · ${it.date}` : ""}{it.duplicate ? " · already present" : ""}</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-muted-foreground" data-testid="rollout-chosen-count">{chosen} item{chosen === 1 ? "" : "s"} will be {pv.applying_to_existing ? "merged in" : "created"}</span>
              <button type="button" data-testid="rollout-apply" onClick={() => apply(false)} disabled={busy || !chosen || (!changeId && !title.trim())} className="pp-btn-dark inline-flex items-center gap-1"><Rocket size={14} /> {busy ? "Applying…" : pv.applying_to_existing ? "Merge into this change" : "Create change"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
